import { useState, useEffect } from 'react';
import { authPostRequest } from 'Api/api';
import SuccessErrorLessTime from 'components/SuccessErrorLessTime';
import { useSelector } from 'react-redux';
import ModalComp from 'components/Modal';
import PropTypes from 'prop-types';
import Loader from 'components/Loader';
import CenteredWrapper from 'pages/updatePassword/CenteredWrapper';
import BracketStrengthForm from '../../components/BracketStrengthForm';
import { isPrecise, integerPartLengthExceeds } from 'utils/utils';
import { useBracketStrengthHook } from './useBracketStrengthHook';

const bracketStrengthInitials = {
  bracketId: null,
  fastenerId: null,
  materialId: null,
  seamId: null,
  sheathingId: null,
  strength: ''
};

const ValidateFields = (bracketInfo, handleError) => {
  if (!bracketInfo?.bracketId) {
    handleError('bracketType', 'Please select bracket type');
  }
  if (!bracketInfo?.materialId) {
    handleError('materialName', 'Please select bracket material');
  }
  if (!bracketInfo?.strength) {
    handleError('strength', 'Please enter bracket strength');
  }
  const negativeBracketStrength = bracketInfo?.strength < 0;
  if (negativeBracketStrength) {
    handleError('strength', 'Please enter positive value for bracket strength ');
  }
  const bracketStrength = isPrecise(bracketInfo?.strength, 2);
  if (bracketStrength) {
    handleError('strength', 'Please enter 2 digits after decimal i.e xxxxx.xx');
  }
  const strengthBeforeDecimal = integerPartLengthExceeds(bracketInfo?.strength, 5);
  if (strengthBeforeDecimal) {
    handleError('strength', 'Please enter 5 digits before decimal i.e xxxxx.xx');
  }
  if (
    bracketInfo?.strength &&
    bracketInfo?.bracketId &&
    bracketInfo?.materialId &&
    !bracketStrength &&
    !negativeBracketStrength &&
    !strengthBeforeDecimal
  ) {
    return true;
  }
  return false;
};

const CreateAndUpdateBracketStrength = ({
  bracket,
  addOpenModal,
  setAddOpenModal,
  setSuccess,
  setAlertBar,
  setIsFormSaved,
  isFormSaved
}) => {
  const [bracketType, setBracketType] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [showAlertBar, setShowAlertBar] = useState(false);
  const [bracketInfo, setBracketInfo] = useState(bracket);
  const [successMessage, setSuccessMessage] = useState([]);
  const [errorMessage, setErrorMessage] = useState([]);
  const [errors, setErrors] = useState({});

  const { userData } = useSelector((store) => store.auth);
  const dropDownOptions = useBracketStrengthHook();

  useEffect(() => {
    setShowLoader(true);
    const ids = new Set();
    let bracketTypeList =
      dropDownOptions.bracketName &&
      dropDownOptions.bracketName?.filter(({ id }) => !ids.has(id) && ids.add(id));
    bracketTypeList && setBracketType(bracketTypeList);
    bracketType != [] && setShowLoader(false);
  }, [dropDownOptions?.bracketName]);

  const handleBracketInfo = (e) => {
    setBracketInfo((prevState) => {
      return { ...prevState, [e.target.name]: e.target.value };
    });
    handleError(e.target.value, null);
  };

  const handleError = (input, errorMessage) => {
    setErrors((prevState) => ({ ...prevState, [input]: errorMessage }));
  };

  //save and add more
  const saveAndAddMore = async (e) => {
    e.preventDefault();
    setSuccess([]);
    setErrorMessage([]);
    setShowAlertBar(false);
    const createBracketStrength = await handleSubmit();
    if (createBracketStrength) {
      setShowAlertBar(true);
      if (createBracketStrength?.resStatus === 1) {
        setIsFormSaved(!isFormSaved);
        setSuccessMessage([createBracketStrength?.resMessage]);
        setBracketInfo(bracketStrengthInitials);
      } else {
        setErrorMessage([createBracketStrength?.resMessage]);
      }
    }
  };

  //Save and close
  const saveAndClose = async (e) => {
    e.preventDefault();
    setErrorMessage([]);
    setSuccess([]);
    setAlertBar(false);
    setShowAlertBar(false);
    const createBracketStrength = await handleSubmit();
    if (createBracketStrength?.resStatus === 1) {
      setIsFormSaved(!isFormSaved);
      setSuccess([createBracketStrength?.resMessage]);

      setBracketInfo(bracketStrengthInitials);
      setAddOpenModal(false);
    }

    if (createBracketStrength?.resStatus === 0) {
      setShowAlertBar(true);
      setErrorMessage([createBracketStrength?.resMessage]);
    }
    setAlertBar(true);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setErrorMessage([]);
    setSuccessMessage([]);
    const isFormValid = ValidateFields(bracketInfo, handleError);

    try {
      const requestParams = {
        userId: userData.userId,
        strengthId: bracket?.strengthId ? bracket.strengthId : '',
        bracketId: bracketInfo.bracketId,
        materialTypeId: bracketInfo.materialId,
        fastenerTypeId: bracketInfo.fastenerId,
        sheathingTypeId: bracketInfo.sheathingId,
        seamTypeId: bracketInfo.seamId,
        strength: bracketInfo.strength
      };
      setShowLoader(true);
      const createBracketPrice =
        isFormValid && (await authPostRequest('saveStrength', requestParams));
      setShowLoader(false);
      return createBracketPrice;
    } catch (err) {
      setErrorMessage([...errorMessage, 'Error']);
      window.scrollTo(0, 0);
    }
  };

  return (
    <CenteredWrapper>
      <ModalComp
        max={800}
        open={addOpenModal}
        setOpen={setAddOpenModal}
        header={bracket?.strengthId ? 'Update Bracket Strength' : 'Add Bracket Strength'}>
        {showAlertBar && (
          <SuccessErrorLessTime
            successMessage={successMessage}
            errorMessage={errorMessage}
            setShowAlertBar={setShowAlertBar}
          />
        )}

        {showLoader && <Loader open={showLoader} />}

        <BracketStrengthForm
          handleSubmit={saveAndAddMore}
          buttonText="Save & Add More"
          buttonClose="Save & Close"
          handleError={handleError}
          handleCancelAction={saveAndClose}
          handleBracketInfo={handleBracketInfo}
          bracketInfo={bracketInfo}
          error={errors}
          bracket={bracket}
          bracketType={bracketType}
        />
      </ModalComp>
    </CenteredWrapper>
  );
};

CreateAndUpdateBracketStrength.propTypes = {
  bracket: PropTypes.object,
  addOpenModal: PropTypes.bool,
  setAddOpenModal: PropTypes.func,
  isFormSaved: PropTypes.bool,
  setIsFormSaved: PropTypes.func,
  setSuccess: PropTypes.func,
  setAlertBar: PropTypes.func
};

export default CreateAndUpdateBracketStrength;
